import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useFinancialStore } from "@/store/useFinancialStore";
import GlassCard from "../components/GlassCard";
import GlassButton from "../components/GlassButton";
import { apiClient } from "@/lib/api";

export default function AISignalsPage() {
  const { aiSignals, marketData, loading } = useFinancialStore();
  const [quantitativeScan, setQuantitativeScan] = useState<any[]>([]);
  const [streamingThought, setStreamingThought] = useState<string>("");
  const [activeSymbol, setActiveSymbol] = useState<string | null>(null);

  useEffect(() => {
    if (activeSymbol) {
      setStreamingThought("Intercepting Neural Stream...");
      const cleanup = apiClient.streamNeuralThoughts(activeSymbol, (thought) => {
        setStreamingThought(prev => prev + " " + thought);
      });
      return cleanup;
    }
  }, [activeSymbol]);

  useEffect(() => {
    const fetchIntelligence = async () => {
      // Sovereign Sync: Fetch real quantitative factor data from backend
      const response = await apiClient.request<any[]>('/ai/analysis/scan');
      if (response.data) {
        setQuantitativeScan(response.data);
      }
      
      // Fetch Real-world Sentiment & Training Stats
      const sentimentResp = await apiClient.request<any[]>('/ai/sentiment');
      if (sentimentResp.data) setGlobalSentiment(sentimentResp.data);

      const statsResp = await apiClient.request<any[]>('/ai/stats');
      if (statsResp.data) setNeuralStats(statsResp.data);
    };

    fetchIntelligence();
  }, []);

  const getRiskColor = (risk: string) => {
    switch (risk?.toUpperCase()) {
      case "LOW":
        return "text-brand-teal-light";
      case "MEDIUM":
        return "text-brand-teal-light";
      case "HIGH":
        return "text-brand-plum-light";
      default:
        return "text-text-secondary";
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30">
      {/* Header */}
      <header className="bg-bg-secondary/50 backdrop-blur-xl border-b border-border-primary sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-text-secondary hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <h1 className="text-xl tracking-tight font-semibold text-text-primary">
                Neural Intelligence Terminal
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-text-secondary font-mono tracking-tighter">
                ACTIVE MODE: <span className="text-brand-teal-light font-bold">SOVEREIGN REALITY</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Real Alpha Intercepts */}
        <div>
          <h2 className="text-lg font-medium text-text-primary mb-6 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${aiSignals.length > 0 ? 'bg-brand-plum-light shadow-glow animate-pulse' : 'bg-white/10'}`} />
            Intercepted Alpha Protocols
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {aiSignals.length > 0 ? aiSignals.map((signal) => {
              const currentPrice = marketData[signal.symbol]?.price || 0;
              return (
                <GlassCard key={signal.id} variant="glow" className="p-6 relative overflow-hidden group">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-text-primary tracking-tight">{signal.symbol}</h3>
                      {currentPrice > 0 && (
                        <p className="text-text-secondary font-mono text-sm">${currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <GlassButton 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setActiveSymbol(signal.symbol)}
                        className={`text-[9px] uppercase tracking-widest ${activeSymbol === signal.symbol ? 'text-brand-teal-light' : 'text-text-tertiary'}`}
                      >
                         Intercept Stream
                      </GlassButton>
                      <div className={`px-3 py-1 rounded-md text-xs font-bold tracking-widest uppercase border ${
                        signal.signal === 'BUY' ? 'bg-brand-teal/20 border-brand-teal/30 text-brand-teal-light' :
                        signal.signal === 'SELL' ? 'bg-brand-plum/20 border-brand-plum/30 text-brand-plum-light' :
                        'bg-white/10 border-white/20 text-text-secondary'
                      }`}>
                        {signal.signal}
                      </div>
                    </div>
                  </div>
                  
                  {activeSymbol === signal.symbol && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mb-6 p-4 bg-bg-primary/50 border border-brand-teal/20 rounded-xl overflow-hidden"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-teal shadow-glow animate-pulse" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-brand-teal-light">Neural Thought Stream</span>
                      </div>
                      <p className="text-xs font-mono text-text-secondary leading-relaxed max-h-32 overflow-y-auto custom-scrollbar">
                        {streamingThought}
                        <span className="inline-block w-1 h-3 bg-brand-teal ml-1 animate-pulse" />
                      </p>
                    </motion.div>
                  )}
                  
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-bold uppercase tracking-widest text-text-secondary">Neural Conviction</span>
                      <span className="text-brand-teal-light font-bold">{(signal.confidence * 100).toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-bg-primary rounded-full h-1.5 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-brand-teal to-brand-teal-light h-full rounded-full transition-all duration-1000" 
                        style={{ width: `${signal.confidence * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  <p className="text-text-secondary text-sm leading-relaxed mb-6 border-l-2 border-brand-teal/20 pl-4 italic">
                    "{signal.reasoning}"
                  </p>
                  
                  <div className="pt-4 border-t border-border-primary flex justify-between items-center">
                    <span className="text-[10px] uppercase tracking-widest text-text-tertiary font-mono">
                      {new Date(signal.timestamp).toISOString()}
                    </span>
                    <GlassButton asChild variant="ghost" size="sm" className="text-brand-teal-light">
                      <Link to={`/trading?symbol=${signal.symbol}&side=${signal.signal}`}>
                        Execute Protocol &rarr;
                      </Link>
                    </GlassButton>
                  </div>
                </GlassCard>
              );
            }) : !loading && (
              <div className="col-span-full py-20 text-center rounded-3xl border border-dashed border-white/5 bg-white/[0.01]">
                <p className="text-xs font-black uppercase tracking-[0.3em] text-text-tertiary opacity-40">No Sovereign Alpha Streams Detected</p>
                <p className="text-[10px] text-text-tertiary mt-2 uppercase">Verify Institutional Backend Core</p>
              </div>
            )}
          </div>
        </div>

        {/* Quantitative Factor Scan - No Placeholders */}
        {quantitativeScan.length > 0 && (
          <GlassCard variant="elevated" className="p-0 overflow-hidden">
            <div className="p-6 border-b border-border-primary bg-bg-secondary/30">
              <h2 className="text-lg font-medium text-text-primary">Quantitative Factor Realization Scan</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-bg-primary/50 text-[10px] tracking-widest uppercase font-bold text-text-secondary">
                    <th className="py-4 px-6 border-b border-border-primary text-center">Technical Score</th>
                    <th className="py-4 px-6 border-b border-border-primary text-center">Fundamental</th>
                    <th className="py-4 px-6 border-b border-border-primary text-center">Sentiment</th>
                    <th className="py-4 px-6 border-b border-border-primary text-center">Overall Score</th>
                    <th className="py-4 px-6 border-b border-border-primary text-center">Risk Vector</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-primary text-sm font-medium">
                  {quantitativeScan.map((item: any) => (
                    <tr key={item.symbol} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="py-4 px-6 text-text-primary text-center">
                         <div className="text-xs font-bold text-text-secondary mb-1">{item.symbol}</div>
                         <div className="text-lg font-mono">{item.technicalScore}/10</div>
                      </td>
                      <td className="py-4 px-6 text-center text-text-primary font-mono">{item.fundamentalScore}/10</td>
                      <td className="py-4 px-6 text-center text-text-primary font-mono">{item.sentimentScore}/10</td>
                      <td className="py-4 px-6 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border-2 border-brand-teal/30 bg-brand-teal/5 text-brand-teal-light font-bold text-base">
                          {item.overallScore}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className={`text-xs font-bold tracking-widest uppercase ${getRiskColor(item.riskLevel)}`}>
                          {item.riskLevel}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
}